import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useFirestoreProject } from '../hooks/useFirestoreProject';
import GeologicalProfile from '../GeologicalProfile';
import CrossFence3DView from './CrossFence3DView';
import { useAuthContext } from '../contexts/AuthContext';
import { serializeProject } from '../utils/projectSerializer';

/**
 * Wrapper: laduje dane projektu uzytkownika z Firestore,
 * lub przyjmuje importowane otwory z route state (z wyszukiwarki),
 * potem renderuje GeologicalProfile z auto-save.
 */
export default function ProjectPage() {
  const { user } = useAuthContext();
  const location = useLocation();
  const { projectId } = useParams();

  const {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    loadingProject,
    loadError,
    loadProject,
    saveProject,
    debouncedSave,
    isSavingToSource,
    lastSavedToSource,
    dirtyBoreholeIds,
    markDirtyBoreholes,
    saveDirtyBoreholesToSource,
    revertBoreholeToSource,
  } = useFirestoreProject(projectId);

  // Stan per linia (fence diagram, fixed 5 linii). Linia 1 = główna, persystowana do Firestore.
  // Pozostałe (2-5) żyją w pamięci sesji.
  const LINE_IDS = [1, 2, 3, 4, 5];
  const [dataByLine, setDataByLine] = useState({ 1: null, 2: null, 3: null, 4: null, 5: null });
  const [activeLine, setActiveLine] = useState(1); // 1..5 lub '3D'
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [dialog, setDialog] = useState(null);
  const latestDataRefByLine = useRef({ 1: null, 2: null, 3: null, 4: null, 5: null });

  const linesWithData = LINE_IDS.filter(id => dataByLine[id] && Array.isArray(dataByLine[id].boreholes) && dataByLine[id].boreholes.length > 0);
  const hasExtraLines = linesWithData.length > 1; // pokaż taby tylko gdy fence aktywny
  const initialData = activeLine === '3D' ? dataByLine[1] : dataByLine[activeLine];

  // Sprawdz czy sa importowane otwory z wyszukiwarki
  const importedBoreholes = location.state?.importedBoreholes;
  const extraLines = location.state?.extraLines; // { 2: [...], 3: [...], ... }
  const autoInterpolate = location.state?.autoInterpolate === true;

  useEffect(() => {
    if (!user) return;

    if (importedBoreholes && importedBoreholes.length > 0) {
      // Importowane otwory z wyszukiwarki
      const next = { 1: { boreholes: importedBoreholes }, 2: null, 3: null, 4: null, 5: null };
      if (extraLines && typeof extraLines === 'object') {
        for (const id of [2, 3, 4, 5]) {
          if (Array.isArray(extraLines[id]) && extraLines[id].length > 0) {
            next[id] = { boreholes: extraLines[id] };
          }
        }
      }
      setDataByLine(next);
      setLoaded(true);
    } else if (projectId) {
      // Laduj konkretny projekt z Firestore (jako Linia 1)
      const load = async () => {
        const data = await loadProject();
        if (data) {
          setDataByLine(prev => ({ ...prev, 1: data }));
        }
        setLoaded(true);
      };
      load();
    } else {
      // Brak projectId w URL (np. /editor bez ID) — pusty stan
      setLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectId]);

  const handleDataChange = useCallback((data) => {
    // Aktualizuj stan AKTYWNEJ linii. Persystencja Firestore tylko dla Linii 1.
    // UWAGA: GeologicalProfile.onDataChange przekazuje FLAT payload (top-level pola), ale
    // initialData wymaga NESTED (initialData.connections.manualConnections, initialData.settings.scale).
    // Trzymamy dwie wersje:
    //   - latestDataRefByLine: FLAT (dla saveProject/markDirtyBoreholes/handleReload)
    //   - dataByLine:          NESTED (dla initialData przy remount GeologicalProfile)
    // Bez konwersji manualConnections/lenses/drawings ginęły przy switch tabów.
    if (activeLine === '3D') return;
    const lineId = activeLine;
    latestDataRefByLine.current[lineId] = data; // FLAT — dla saveProject
    setDataByLine(prev => ({ ...prev, [lineId]: serializeProject(data) })); // NESTED — dla initialData
    if (lineId === 1) {
      debouncedSave(data);
      if (data?.boreholes) {
        markDirtyBoreholes(data.boreholes);
      }
    }
  }, [activeLine, debouncedSave, markDirtyBoreholes]);

  const handleSwitchLine = useCallback((line) => {
    if (line === activeLine) return;
    setActiveLine(line);
    setReloadKey((k) => k + 1); // remount GeologicalProfile z nowym initialData
  }, [activeLine]);

  const handleManualSave = useCallback(() => {
    // Zapis do Firestore tylko dla Linii 1 (głównej)
    const data = latestDataRefByLine.current[1];
    if (data) {
      saveProject(data);
    }
  }, [saveProject]);

  const handleSaveToSource = useCallback(async () => {
    const data = latestDataRefByLine.current[1] || dataByLine[1];
    const boreholes = data?.boreholes || [];
    if (boreholes.length === 0) {
      setDialog({ type: 'info', title: 'Brak otworów', message: 'Brak otworów do zapisania.' });
      return;
    }

    const dirtyCount = dirtyBoreholeIds?.size || 0;
    if (dirtyCount === 0) {
      setDialog({
        type: 'success',
        title: 'Wszystko aktualne',
        message: 'Brak niezapisanych zmian w otworach — wszystkie są zsynchronizowane z bazą.',
      });
      return;
    }

    // Lista nazw zmienionych otworów (max 10 pokazanych, reszta jako "+N więcej")
    const dirtyNames = boreholes
      .filter((b) => dirtyBoreholeIds.has(b.id))
      .map((b) => b.name || b.id)
      .slice(0, 10);
    const extra = dirtyCount - dirtyNames.length;

    setDialog({
      type: 'confirm',
      title: 'Zapisać do bazy otworów?',
      message: `Zostanie zapisanych ${dirtyCount} zmieniony${dirtyCount === 1 ? '' : 'ch'} otwor${dirtyCount === 1 ? '' : 'ów'}:`,
      list: extra > 0 ? [...dirtyNames, `+${extra} więcej…`] : dirtyNames,
      warning: 'Dane warstw i wody w bazie zostaną nadpisane.',
      confirmLabel: 'Zapisz do bazy',
      onConfirm: async () => {
        setDialog({ type: 'saving', title: 'Zapisywanie…', message: 'Trwa zapis do bazy otworów.' });
        const result = await saveDirtyBoreholesToSource(boreholes);
        if (result.errors > 0) {
          setDialog({
            type: 'error',
            title: 'Zapisano z błędami',
            message: `Zapisano: ${result.saved}. Błędy: ${result.errors}.`,
            errors: result.errorMessages || [],
          });
        } else {
          setDialog({
            type: 'success',
            title: 'Zapisano',
            message: `Pomyślnie zapisano ${result.saved} otwor${result.saved === 1 ? '' : 'ów'} do bazy.`,
          });
        }
      },
    });
  }, [dataByLine, dirtyBoreholeIds, saveDirtyBoreholesToSource]);

  const handleReload = useCallback(async () => {
    // Reload zawsze dotyczy Linii 1 (głównej, persystowanej).
    const doReload = async () => {
      if (projectId) {
        // Tryb projektu z URL — pełny load z Firestore
        const data = await loadProject();
        if (data) {
          setDataByLine(prev => ({ ...prev, 1: data }));
          setReloadKey((k) => k + 1);
        }
        return;
      }
      // Tryb importu (brak projectId) — odświeżamy każdy otwór z jego sourceBoreholeId
      const current = latestDataRefByLine.current[1] || dataByLine[1]; // FLAT preferowane; fallback NESTED
      const boreholes = current?.boreholes || [];
      const refreshable = boreholes.filter((b) => b.sourceProjectId && b.sourceBoreholeId);
      if (refreshable.length === 0) {
        setDialog({
          type: 'info',
          title: 'Brak otworów do odświeżenia',
          message: 'Żaden z otworów nie ma referencji do bazy źródłowej (sourceBoreholeId).',
        });
        return;
      }
      setDialog({ type: 'saving', title: 'Odświeżanie…', message: `Pobieranie ${refreshable.length} otwor${refreshable.length === 1 ? 'u' : 'ów'} z bazy.` });
      const results = await Promise.allSettled(refreshable.map((b) => revertBoreholeToSource(b)));
      const refreshedById = new Map();
      let successCount = 0;
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) {
          refreshedById.set(refreshable[i].id, r.value);
          successCount += 1;
        }
      });
      // Merge: świeże dane z bazy + zachowane kolory warstw z bieżącego stanu (per sourceLayerId).
      // Bez tego refresh nadpisywał kolor wyliczony z catNrGeologyLayer / algorytmu, gubiąc
      // wszystko co użytkownik miał na ekranie (nawet jeśli "nic nie robił" — np. kolory dziedziczone
      // z innego projektu albo z poprzedniego cyklu zapis/odczyt nie zostawały).
      const newBoreholes = boreholes.map((b) => {
        const fresh = refreshedById.get(b.id);
        if (!fresh) return b;
        const oldLayersById = new Map((b.layers || []).map((l) => [l.sourceLayerId, l]));
        return {
          ...fresh,
          layers: (fresh.layers || []).map((fl) => {
            const old = oldLayersById.get(fl.sourceLayerId);
            return old?.color ? { ...fl, color: old.color } : fl;
          }),
        };
      });
      // dataByLine musi być NESTED (initialData wymaga nested). Konwertuj jeśli current to flat.
      const mergedFlat = { ...current, boreholes: newBoreholes };
      setDataByLine(prev => ({ ...prev, 1: serializeProject(mergedFlat) }));
      latestDataRefByLine.current[1] = mergedFlat;
      setReloadKey((k) => k + 1);
      const failedCount = refreshable.length - successCount;
      if (failedCount > 0) {
        setDialog({
          type: 'error',
          title: 'Odświeżono częściowo',
          message: `Pobrano ${successCount} z ${refreshable.length}. Nie udało się odświeżyć ${failedCount} otwor${failedCount === 1 ? 'u' : 'ów'} (sprawdź połączenie).`,
        });
      } else {
        setDialog({
          type: 'success',
          title: 'Odświeżono',
          message: `Pobrano świeże dane ${successCount} otwor${successCount === 1 ? 'u' : 'ów'} z bazy.`,
        });
      }
    };
    if (hasUnsavedChanges) {
      setDialog({
        type: 'confirm',
        title: 'Odświeżyć z bazy?',
        message: 'Masz niezapisane zmiany. Pobranie danych z bazy je nadpisze.',
        warning: 'Tej operacji nie da się cofnąć.',
        confirmLabel: 'Odśwież i nadpisz',
        onConfirm: doReload,
      });
      return;
    }
    await doReload();
  }, [projectId, hasUnsavedChanges, loadProject, revertBoreholeToSource, dataByLine]);

  const getSaveStatus = () => {
    if (isSaving) return 'Zapisywanie...';
    if (hasUnsavedChanges) return 'Niezapisane zmiany';
    if (lastSaved) return `Zapisano ${lastSaved.toLocaleTimeString('pl-PL')}`;
    return '';
  };

  const getSourceSaveStatus = () => {
    if (isSavingToSource) return 'Zapisywanie do bazy otworów...';
    if (dirtyBoreholeIds && dirtyBoreholeIds.size > 0) {
      return `Niezapisane w bazie: ${dirtyBoreholeIds.size} — kliknij "Zapisz do bazy otworów"`;
    }
    if (lastSavedToSource) return `Baza otworów zapisana ${lastSavedToSource.toLocaleTimeString('pl-PL')}`;
    return '';
  };

  // Wrapper: po przywróceniu otworu z bazy podmień go w bieżącym stanie projektu
  const handleRevertBorehole = useCallback(async (borehole) => {
    const fresh = await revertBoreholeToSource(borehole);
    if (!fresh) {
      setDialog({
        type: 'error',
        title: 'Błąd pobierania',
        message: 'Nie udało się pobrać otworu z bazy — sprawdź połączenie.',
      });
      return null;
    }
    return fresh;
  }, [revertBoreholeToSource]);

  if (!importedBoreholes && loadingProject) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500 text-lg">Ladowanie projektu...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-red-500 text-lg">{loadError}</div>
      </div>
    );
  }

  if (!loaded) return null;

  return (
    <>
      {hasExtraLines && (
        <FenceTabSwitcher
          activeLine={activeLine}
          onSwitch={handleSwitchLine}
          dataByLine={dataByLine}
          lineIds={LINE_IDS}
        />
      )}
      {activeLine === '3D' ? (
        <CrossFence3DView dataByLine={dataByLine} />
      ) : (
        <GeologicalProfile
          key={`L${activeLine}-${reloadKey}`}
          initialData={initialData}
          onDataChange={handleDataChange}
          onSave={handleManualSave}
          onReload={handleReload}
          onSaveToSource={handleSaveToSource}
          saveStatus={getSaveStatus()}
          sourceSaveStatus={getSourceSaveStatus()}
          dirtyBoreholeIds={dirtyBoreholeIds}
          onRevertBorehole={handleRevertBorehole}
          isImported={!!importedBoreholes}
          autoInterpolate={autoInterpolate}
        />
      )}
      <AppDialog dialog={dialog} onClose={() => setDialog(null)} />
    </>
  );
}

// Paleta linii (musi być spójna z BoreholeBrowserPage.LINE_COLORS)
const LINE_PALETTE = {
  1: { tabActive: 'bg-purple-50 text-purple-700 border-purple-600', tabHover: 'hover:text-purple-700 hover:bg-purple-50', label: 'fioletowa' },
  2: { tabActive: 'bg-sky-50 text-sky-700 border-sky-600',          tabHover: 'hover:text-sky-700 hover:bg-sky-50',       label: 'cyan' },
  3: { tabActive: 'bg-orange-50 text-orange-700 border-orange-600', tabHover: 'hover:text-orange-700 hover:bg-orange-50', label: 'pomarańczowa' },
  4: { tabActive: 'bg-pink-50 text-pink-700 border-pink-600',       tabHover: 'hover:text-pink-700 hover:bg-pink-50',     label: 'różowa' },
  5: { tabActive: 'bg-lime-50 text-lime-700 border-lime-600',       tabHover: 'hover:text-lime-700 hover:bg-lime-50',     label: 'limonkowa' },
};

/**
 * Pasek zakładek widoczny tylko gdy aktywny jest tryb fence diagram (Linia B istnieje).
 * Renderuje się NAD GeologicalProfile (sticky top), żeby user mógł szybko przełączać.
 * Linia A — fioletowa (persystowana do Firestore), Linia B — cyan (lokalna, w pamięci sesji).
 */
function FenceTabSwitcher({ activeLine, onSwitch, dataByLine, lineIds }) {
  const tabBase = 'px-3 py-2 rounded-t-lg text-sm font-medium transition border-b-2';
  // Pokaż tylko taby dla linii które mają dane
  const visibleLines = lineIds.filter(id => dataByLine[id] && dataByLine[id].boreholes && dataByLine[id].boreholes.length > 0);
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
      }}
      className="px-4 pt-2 flex items-center gap-1 flex-wrap"
    >
      <span className="text-xs text-gray-500 mr-2">🔀 Tryb krzyżowy (fence):</span>
      {visibleLines.map(id => {
        const palette = LINE_PALETTE[id];
        const count = dataByLine[id]?.boreholes?.length || 0;
        const isActive = activeLine === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSwitch(id)}
            className={`${tabBase} ${
              isActive
                ? palette.tabActive
                : `bg-transparent text-gray-500 border-transparent ${palette.tabHover}`
            }`}
            title={id === 1 ? 'Linia 1 — persystowana do Firestore' : `Linia ${id} — w pamięci sesji (nie zapisuje się do Firestore)`}
          >
            L{id} ({count}) {isActive && '●'}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => onSwitch('3D')}
        className={`${tabBase} ${
          activeLine === '3D'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-600'
            : 'bg-transparent text-gray-500 border-transparent hover:text-emerald-700 hover:bg-emerald-50'
        }`}
        title="Podgląd 3D wszystkich linii krzyżowo (fence diagram). Tylko odczyt."
      >
        📐 Podgląd 3D {activeLine === '3D' && '●'}
      </button>
      {typeof activeLine === 'number' && activeLine !== 1 && (
        <span className="ml-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
          ⚠ Linia {activeLine} nie zapisuje się do Firestore — edycje znikną po reload
        </span>
      )}
      {activeLine === '3D' && (
        <span className="ml-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
          🎬 Obracaj LPM • Zoom scroll • Pan PPM
        </span>
      )}
    </div>
  );
}

/**
 * Modal w stylu LayerEditDialog — różne typy: info / confirm / saving / success / error.
 * Zamienia natywne window.alert/confirm na spójny wizualnie dialog.
 */
function AppDialog({ dialog, onClose }) {
  if (!dialog) return null;

  const { type = 'info', title, message, warning, list, errors, onConfirm, confirmLabel } = dialog;

  // Wariant kolorystyczny per typ
  const variants = {
    info:    { border: '#3B82F6', icon: 'ℹ',  iconBg: '#DBEAFE', iconColor: '#1E40AF' },
    confirm: { border: '#F59E0B', icon: '?',  iconBg: '#FEF3C7', iconColor: '#92400E' },
    saving:  { border: '#3B82F6', icon: '⟳',  iconBg: '#DBEAFE', iconColor: '#1E40AF' },
    success: { border: '#10B981', icon: '✓',  iconBg: '#D1FAE5', iconColor: '#065F46' },
    error:   { border: '#EF4444', icon: '!',  iconBg: '#FEE2E2', iconColor: '#991B1B' },
  };
  const v = variants[type] || variants.info;
  const isConfirm = type === 'confirm';
  const isSaving = type === 'saving';

  const handleKey = (e) => { if (e.key === 'Escape' && !isSaving) onClose(); };

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999 }}
        onClick={isSaving ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        onKeyDown={handleKey}
        tabIndex={-1}
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'white',
          padding: '24px',
          border: `2px solid ${v.border}`,
          borderRadius: '12px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          width: '480px',
          maxWidth: '92vw',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        <div className="flex items-start gap-4 mb-4">
          <div
            aria-hidden="true"
            style={{
              flex: '0 0 auto',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: v.iconBg,
              color: v.iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              fontWeight: 700,
              animation: isSaving ? 'app-dialog-spin 1s linear infinite' : 'none',
            }}
          >
            {v.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 mb-1">{title}</h3>
            {message && <p className="text-sm text-gray-600 whitespace-pre-line">{message}</p>}
          </div>
        </div>

        {Array.isArray(list) && list.length > 0 && (
          <ul className="mb-3 ml-1 max-h-40 overflow-y-auto text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-2">
            {list.map((item, i) => (
              <li key={i} className="py-0.5">• {item}</li>
            ))}
          </ul>
        )}

        {warning && (
          <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            ⚠ {warning}
          </div>
        )}

        {Array.isArray(errors) && errors.length > 0 && (
          <ul className="mb-4 max-h-40 overflow-y-auto text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {errors.map((err, i) => (
              <li key={i} className="py-0.5">• {err}</li>
            ))}
          </ul>
        )}

        {!isSaving && (
          <div className="flex gap-3 mt-4">
            {isConfirm ? (
              <>
                <button
                  type="button"
                  onClick={() => { onClose(); if (onConfirm) onConfirm(); }}
                  className="flex-1 bg-brand-700 text-white px-4 py-2 rounded font-semibold hover:bg-brand-800 transition"
                  autoFocus
                >
                  ✓ {confirmLabel || 'Zatwierdź'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-300 transition"
                >
                  ✕ Anuluj
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-brand-700 text-white px-4 py-2 rounded font-semibold hover:bg-brand-800 transition"
                autoFocus
              >
                OK
              </button>
            )}
          </div>
        )}

        {isSaving && (
          <div className="text-xs text-gray-500 text-center mt-2">
            Proszę nie zamykać okna…
          </div>
        )}
      </div>
      <style>{`@keyframes app-dialog-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
